import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Gift, Sparkles, Award, CalendarClock, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, siteUrl } from "@/lib/api";
import { EmptyState } from "@/components/dm/EmptyState";
import { ShareActions } from "@/components/dm/ShareActions";
import { AnkaraStrip, useDate } from "@/components/dm/ui";

export const InviteCard = () => {
  const { t } = useApp();
  const [live, setLive] = useState(false);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("dm_invite_hidden") === "1");
  useEffect(() => { if (!hidden) api.get("/referral/campaign").then((r) => setLive(r.data.live)).catch(() => {}); }, [hidden]);
  if (!live || hidden) return null;
  return (
    <div data-testid="invite-card" className="relative flex items-center gap-3 rounded-[20px] bg-[#FEF3C7] border-2 border-[#F59E0B]/60 p-4 pr-12">
      <Gift className="w-8 h-8 text-[#C2410C] shrink-0" aria-hidden="true" />
      <p className="flex-1 font-extrabold text-[#291E18]">{t("invite_banner")}</p>
      <Link to="/invite" data-testid="invite-card-cta" className="press min-h-[44px] px-5 inline-flex items-center rounded-full bg-[#1E4D3B] text-white font-bold">{t("invite_cta")}</Link>
      <button data-testid="invite-card-hide" aria-label={t("dismiss")} onClick={() => { sessionStorage.setItem("dm_invite_hidden", "1"); setHidden(true); }} className="absolute top-1 right-1 w-10 h-10 flex items-center justify-center rounded-full"><X className="w-4 h-4" /></button>
    </div>
  );
};

const Reward = ({ icon: I, title, body }) => (
  <div className="rounded-[20px] bg-[#FFFDF9] border border-[#E8E2D9] p-4 space-y-1">
    <I className="w-7 h-7 text-[#D97706]" aria-hidden="true" />
    <div className="font-extrabold">{title}</div>
    <p className="text-sm text-[#6B5E55]">{body}</p>
  </div>
);

export default function Invite() {
  const { t, user, ready, setAuth } = useApp();
  const date = useDate();
  const [d, setD] = useState(null);
  useEffect(() => { if (user) api.get("/me/referral").then((r) => setD(r.data)); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ready) return null;
  if (!user) return <div className="max-w-xl mx-auto p-6"><EmptyState title={t("signup_prompt")}><button data-testid="invite-signup" onClick={() => setAuth({ mode: "prompt" })} className="press min-h-[52px] px-8 rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold">{t("create_account")}</button></EmptyState></div>;
  if (!d) return <div className="max-w-3xl mx-auto p-4"><div className="skeleton h-64 rounded-[28px]" /></div>;
  const c = d.campaign;
  const link = `${siteUrl()}/?ref=${d.code}`;
  const msg = t("invite_msg", { name: user.name.split(" ")[0] });
  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6" data-testid="invite-page">
      <div className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight inline-flex items-center gap-2"><Gift className="w-8 h-8 text-[#C2410C]" />{t("invite_title")}</h1>
        <p className="text-[#6B5E55]">{t("invite_sub")}</p>
        <p data-testid="invite-campaign-status" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold ${c.live ? "bg-[#1E4D3B] text-[#FDE68A]" : "bg-[#F3EDE4] text-[#6B5E55]"}`}>
          <CalendarClock className="w-4 h-4" />{c.live ? (c.end ? t("invite_live_until", { date: date(c.end + "T12:00:00", { day: "numeric", month: "long" }) }) : t("invite_live")) : t("invite_off")}
        </p>
      </div>
      <ShareActions link={link} msg={msg} target="invite" prefix="invite" />
      <section className="rounded-[28px] overflow-hidden bg-[#1E4D3B] text-white">
        <AnkaraStrip />
        <div className="p-6 flex flex-col sm:flex-row items-center gap-6">
          <div className="bg-[#FFFDF9] p-3 rounded-3xl shrink-0" data-testid="invite-qr"><QRCodeCanvas value={link} size={170} level="M" fgColor="#1E4D3B" bgColor="#FFFDF9" marginSize={1} /></div>
          <div className="space-y-2 min-w-0 text-center sm:text-left">
            <div className="text-sm font-bold text-[#FDE68A]">{t("invite_your_link")}</div>
            <div data-testid="invite-link" className="font-mono text-sm break-all bg-white/10 rounded-2xl px-3 py-2">{link.replace(/^https?:\/\//, "")}</div>
            <div data-testid="invite-code" className="text-2xl font-extrabold text-[#F59E0B] tracking-widest">{t("invite_code", { code: d.code })}</div>
          </div>
        </div>
      </section>
      {d.boost_credits > 0 && <p data-testid="invite-credits" className="rounded-2xl bg-[#FEF3C7] p-4 font-bold">{t("invite_credit", { n: d.boost_credits })}</p>}
      <div className="grid sm:grid-cols-3 gap-3">
        <Reward icon={Sparkles} title={t("reward_featured")} body={t("reward_featured_d", { n: c.featured_days })} />
        <Reward icon={Award} title={t("reward_badge")} body={t("reward_badge_d")} />
        <Reward icon={CalendarClock} title={t("reward_long")} body={t("reward_long_d", { n: c.long_days })} />
      </div>
      <section className="space-y-2">
        <h2 className="text-base md:text-lg font-extrabold">{t("invite_how")}</h2>
        <ol className="space-y-2">{[t("invite_step1"), t("invite_step2"), t("invite_step3", { n: c.window_days })].map((s, i) => (
          <li key={i} className="flex gap-3 items-start"><span className="w-8 h-8 rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold flex items-center justify-center shrink-0">{i + 1}</span><span className="pt-1">{s}</span></li>))}</ol>
        <p className="text-xs text-[#6B5E55]">{t("invite_rules", { n: c.max_rewards })}</p>
      </section>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-[#FFFDF9] border p-4" data-testid="invite-joined"><div className="text-xs font-semibold text-[#6B5E55]">{t("invite_joined")}</div><div className="text-3xl font-extrabold text-[#1E4D3B]">{d.joined}</div></div>
        <div className="rounded-2xl bg-[#FFFDF9] border p-4" data-testid="invite-rewarded"><div className="text-xs font-semibold text-[#6B5E55]">{t("invite_rewarded")}</div><div className="text-3xl font-extrabold text-[#D97706]">{d.rewarded}</div></div>
      </div>
      {d.friends.length > 0 && (
        <section className="space-y-2"><h2 className="text-base md:text-lg font-extrabold">{t("invite_friends")}</h2>
          <ul className="space-y-2">{d.friends.map((f, i) => (
            <li key={i} data-testid={`invite-friend-${i}`} className="flex justify-between items-center gap-3 rounded-2xl bg-[#FFFDF9] border px-4 py-3"><span className="font-semibold truncate">{f.name}</span>
              <span className={`rounded-full px-3 py-0.5 text-xs font-bold ${f.status === "rewarded" ? "bg-[#1E4D3B] text-[#FDE68A]" : "bg-[#F3EDE4]"}`}>{t(`ref_${f.status}`)}</span></li>))}</ul>
        </section>
      )}
    </div>
  );
}
